// GC navigation: desktop dropdown panels and the mobile slide-in menu share one DOM (blocks/_gc-nav.liquid).
const DESKTOP = matchMedia('(min-width: 1040px)');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const SIZES = [
  ['.gc-nav__rows', '(min-width: 1040px) 60px, 72px'],
  ['.gc-nav__occasions', '(min-width: 1040px) 172px, 50vw'],
  ['.gc-nav__spots', '150px'],
  ['.gc-nav__products', '(min-width: 1040px) 248px, 50vw'],
  ['.gc-nav__carousel', '60px'],
  ['.gc-nav-group', '(min-width: 1040px) 70px, 25vw'],
  ['.gc-nav-start', '50vw'],
  ['.gc-nav__script', '290px'],
];
// images only one layout ever shows: the other layout never fetches them
const DESKTOP_ONLY = '.gc-nav__script, .gc-nav__spots';
const PHONE_ONLY = '.gc-nav__home, .gc-nav__panel--gifts .gc-nav__loved';
const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
const later = (fn, ms) => (REDUCED.matches ? fn() : setTimeout(fn, ms));

class GcNav extends HTMLElement {
  connectedCallback() {
    this.header = document.getElementById('header-component');
    this.sheet = this.querySelector('.gc-nav__sheet');
    this.scrim = this.querySelector('.gc-nav__scrim');
    this.carousel = this.querySelector('[data-gc-carousel]');
    this.active = null;
    this.view = 'home';
    this.ac = new AbortController();
    const o = { signal: this.ac.signal };
    // the full stylesheet arrives without blocking the page (_gc-nav.liquid); a panel opened before it lands waits for it
    const css = document.querySelector('link[data-gc-nav-css]');
    this.styled = !css || !!css.sheet;
    this.css = this.styled ? Promise.resolve() : new Promise((r) => css.addEventListener('load', () => css.sheet && ((this.styled = true), r()), o));
    for (const li of this.querySelectorAll('.gc-nav__item[data-gc-panel]')) {
      li.addEventListener('pointerenter', (e) => this.enter(e, li), o);
      li.addEventListener('pointerleave', (e) => this.leave(e), o);
      li.querySelector('[data-gc-trigger]').addEventListener('click', (e) => this.tap(e, li), o);
      li.querySelector('[data-gc-trigger]').addEventListener('keydown', (e) => e.key === 'ArrowDown' && (e.preventDefault(), this.open(li, true)), o);
      li.querySelector('[data-gc-toggle]').addEventListener('click', () => (this.active === li ? this.close(true) : this.open(li)), o);
      li.addEventListener('focusout', (e) => DESKTOP.matches && this.active === li && !li.contains(e.relatedTarget) && this.close(), o);
    }
    this.addEventListener('pointerdown', (e) => (this.pointer = e.pointerType), o);
    this.addEventListener('pointerover', () => this.warm(), o);
    this.addEventListener('focusin', () => this.warm(), o);
    this.addEventListener('click', (e) => this.act(e), o);
    this.carousel?.addEventListener('scroll', () => this.progress(), { ...o, passive: true });
    document.addEventListener('keydown', (e) => this.key(e), o);
    document.addEventListener('pointerdown', (e) => this.active && !this.contains(e.target) && this.close(), o);
    // the scrim closes on click, not pointerdown, so the click cannot fall through to the page beneath
    this.scrim?.addEventListener('click', () => this.close(), o);
    document.addEventListener('click', (e) => { const b = e.target.closest?.('[data-gc-burger]'); if (b) this.openDrawer(b); }, o);
    document.addEventListener('focusin', (e) => this.guard(e), o);
    document.addEventListener('shopify:block:select', (e) => this.select(e), o);
    document.addEventListener('shopify:block:deselect', () => this.reset(), o);
    window.addEventListener('scroll', () => this.active && !this.pinned && Math.abs(scrollY - this.y0) > 24 && this.close(), { ...o, passive: true });
    window.addEventListener('pageshow', () => this.reset(), o);
    DESKTOP.addEventListener('change', () => this.mode(), o);
    this.mode();
  }

  disconnectedCallback() {
    this.ac.abort();
    clearTimeout(this.timer);
    clearTimeout(this.closing);
    this.want = null;
    this.unlock();
    document.documentElement.removeAttribute('data-gc-nav-panel');
  }

  mode() {
    this.reset();
    if (this.header) this.header.dataset.menuStyle = DESKTOP.matches ? 'menu' : 'drawer';
  }

  reset() {
    this.pinned = false;
    this.close(false, true);
    this.closeDrawer(true);
  }

  // first reach for the menu loads its images; the rest follow when the browser is idle
  warm() {
    if (this.warmed) return;
    this.warmed = true;
    idle(() => this.hydrate(this));
  }

  // urgent: the screen is opening now, so its images jump the queue
  hydrate(root, urgent = false) {
    if (!root) return; // a block hidden in the editor
    const skip = DESKTOP.matches ? PHONE_ONLY : DESKTOP_ONLY;
    // sources first, so a reduced-motion visitor's browser never starts on the animated GIF
    for (const s of root.querySelectorAll('source[data-srcset]')) {
      if (s.closest(skip)) continue;
      s.srcset = s.dataset.srcset;
      s.removeAttribute('data-srcset');
    }
    for (const img of root.querySelectorAll('img[data-src]')) {
      if (img.closest(skip)) continue;
      const src = img.dataset.src;
      const widths = (img.dataset.widths || '').split(',').map(Number).filter(Boolean);
      img.sizes = this.sizes(img);
      if (urgent) img.fetchPriority = 'high';
      if (widths.length) img.srcset = widths.map((w) => `${src.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');
      img.src = src;
      img.removeAttribute('data-src');
    }
  }

  sizes(img) {
    const tile = img.closest('.gc-nav-tile');
    if (!tile) return (SIZES.find(([sel]) => img.closest(sel)) || [, '100vw'])[1];
    // shape cutouts are sized by height (--gc-h), so a tall narrow glass needs far fewer pixels than its tile is wide
    const h = parseFloat(tile.style.getPropertyValue('--gc-h')) || 52;
    const a = (+img.getAttribute('width') || 1) / (+img.getAttribute('height') || 1);
    return `(min-width: 1040px) ${Math.ceil(h * 0.944 * a)}px, ${Math.ceil(Math.max(h * 1.113, 63) * a)}px`;
  }

  // ---------- desktop ----------
  enter(e, li) {
    if (!DESKTOP.matches || e.pointerType === 'touch') return;
    clearTimeout(this.timer);
    if (this.active !== li) this.timer = setTimeout(() => this.open(li), this.active ? 0 : 80);
  }

  leave(e) {
    if (!DESKTOP.matches || e.pointerType === 'touch' || this.pinned) return;
    clearTimeout(this.timer);
    this.want = null;
    if (this.active) this.timer = setTimeout(() => this.close(), 220);
  }

  tap(e, li) {
    if (DESKTOP.matches && this.pointer === 'touch' && this.active !== li) {
      e.preventDefault();
      this.open(li);
    }
  }

  open(li, focusFirst = false) {
    if (!DESKTOP.matches) return;
    if (!this.styled) {
      this.want = li;
      this.css.then(() => this.want === li && this.open(li, focusFirst));
      return;
    }
    if (this.active && this.active !== li) this.close(false, true);
    const panel = li.querySelector('.gc-nav__panel');
    this.hydrate(panel, true);
    const top = this.header?.getBoundingClientRect().bottom || 0;
    const left = this.header?.querySelector('.header__columns');
    this.style.setProperty('--gc-nav-top', `${Math.round(top)}px`);
    if (left) this.style.setProperty('--gc-nav-left', getComputedStyle(left).paddingLeft);
    li.dataset.active = '';
    panel.removeAttribute('data-closing');
    panel.dataset.open = '';
    li.querySelectorAll('[aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    this.active = li;
    this.y0 = scrollY;
    if (this.hasAttribute('data-scrim')) this.scrim.hidden = false;
    document.documentElement.setAttribute('data-gc-nav-panel', '');
    if (focusFirst) [...panel.querySelectorAll('a[href], button')].find((n) => n.getClientRects().length)?.focus();
  }

  close(returnFocus = false, instant = false) {
    const li = this.active;
    if (!li) return;
    const panel = li.querySelector('.gc-nav__panel');
    this.active = null;
    delete li.dataset.active;
    li.querySelectorAll('[aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.scrim.hidden = true;
    document.documentElement.removeAttribute('data-gc-nav-panel');
    document.dispatchEvent(new Event('gc-nav:closed'));
    delete panel.dataset.open;
    if (!instant) {
      panel.dataset.closing = '';
      later(() => panel.removeAttribute('data-closing'), 160);
    }
    if (returnFocus) li.querySelector('[data-gc-toggle]').focus();
  }

  // ---------- mobile ----------
  // opener: the burger that was tapped (Safari does not focus buttons on tap, so activeElement is not enough)
  openDrawer(opener) {
    if (DESKTOP.matches || this.hasAttribute('data-drawer-open')) return;
    if (!this.styled) return void this.css.then(() => this.openDrawer(opener));
    clearTimeout(this.closing); // reopened mid slide-out: keep the lock that close had not released yet
    this.hydrate(this.querySelector('.gc-nav__home'), true);
    this.fillCarousel();
    this.lastFocus = opener || document.activeElement;
    this.removeAttribute('data-drawer-closing');
    this.dataset.drawerOpen = '';
    this.sheet.setAttribute('role', 'dialog');
    this.sheet.setAttribute('aria-modal', 'true');
    this.sheet.setAttribute('aria-label', this.sheet.dataset.gcLabel || 'Menu');
    document.documentElement.setAttribute('data-gc-nav-open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    // Kaching Cart removes `inert` from main and footer, so aria-hidden backs it up for screen readers
    if (!this.inerted?.length) {
      this.inerted = [...document.querySelectorAll('#MainContent, footer, #header-group > :not(.header-section)')].filter((n) => !n.inert && !n.hasAttribute('aria-hidden'));
      this.inerted.forEach((n) => ((n.inert = true), n.setAttribute('aria-hidden', 'true')));
    }
    document.querySelectorAll('[data-gc-burger]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    this.show('home', true);
    requestAnimationFrame(() => {
      this.progress();
      this.querySelector('.gc-nav__home [data-gc-close]')?.focus({ preventScroll: true });
    });
    idle(() => this.hydrate(this));
  }

  closeDrawer(instant = false) {
    if (!this.hasAttribute('data-drawer-open')) return;
    this.removeAttribute('data-drawer-open');
    document.querySelectorAll('[data-gc-burger]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.dispatchEvent(new Event('gc-nav:closed'));
    // unlock only once the slide-out ends: until then the header must keep its containment released,
    // or the fixed sheet would shrink into the header box mid-animation
    const done = () => {
      this.unlock();
      this.removeAttribute('data-drawer-closing');
      this.show('home', true);
    };
    if (instant) return void done();
    this.dataset.drawerClosing = '';
    this.lastFocus?.focus?.({ preventScroll: true });
    if (REDUCED.matches) done();
    else this.closing = setTimeout(done, 250);
  }

  overlay() {
    return !!document.querySelector('dialog[open]');
  }

  unlock() {
    document.documentElement.removeAttribute('data-gc-nav-open');
    if (!document.querySelector('dialog[open]')) document.documentElement.removeAttribute('scroll-lock');
    (this.inerted || []).forEach((n) => ((n.inert = false), n.removeAttribute('aria-hidden')));
    this.inerted = [];
    this.sheet?.removeAttribute('role');
    this.sheet?.removeAttribute('aria-modal');
    this.sheet?.removeAttribute('aria-label');
  }

  show(name, instant = false) {
    for (const p of this.querySelectorAll('.gc-nav__panel')) {
      if (p.dataset.gcView === name) {
        p.removeAttribute('data-closing');
        p.dataset.open = '';
        p.scrollTop = 0;
        this.hydrate(p, true);
      } else if (p.hasAttribute('data-open')) {
        p.removeAttribute('data-open');
        if (!instant) {
          p.dataset.closing = '';
          later(() => p.removeAttribute('data-closing'), 230);
        }
      }
    }
    this.view = name;
    const home = this.querySelector('.gc-nav__home');
    if (home) home.inert = name !== 'home';
    if (name !== 'home' && !instant) requestAnimationFrame(() => this.querySelector(`#gc-nav-${name} [data-gc-back]`)?.focus({ preventScroll: true }));
  }

  act(e) {
    const t = e.target.closest('[data-gc-open], [data-gc-back], [data-gc-close], [data-gc-search]');
    if (!t || DESKTOP.matches) return;
    if (t.dataset.gcOpen) this.show(t.dataset.gcOpen);
    else if (t.hasAttribute('data-gc-back')) {
      const from = this.view;
      this.show('home');
      this.querySelector(`.gc-nav__home [data-gc-open="${from}"]`)?.focus({ preventScroll: true });
    } else if (t.hasAttribute('data-gc-close')) this.closeDrawer();
    else if (t.hasAttribute('data-gc-search')) {
      // the theme's search opens over the menu (top layer); closing it lands back in the menu
      const modal = document.getElementById('search-modal');
      if (!modal?.showDialog) return void (location.href = `${window.Shopify?.routes?.root || '/'}search`);
      modal.addEventListener('dialog:close', () => this.hasAttribute('data-drawer-open') && t.focus({ preventScroll: true }), { once: true });
      modal.showDialog();
    }
  }

  fillCarousel() {
    if (!this.carousel || this.carousel.childElementCount) return;
    for (const tile of this.querySelectorAll('#gc-nav-shape .gc-nav-tile')) {
      const c = tile.cloneNode(true);
      c.removeAttribute('data-shopify-editor-block');
      c.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      this.carousel.append(c);
    }
    this.hydrate(this.carousel, true);
  }

  progress() {
    const c = this.carousel;
    if (!c) return;
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => {
      const vis = Math.min(1, c.clientWidth / (c.scrollWidth || 1));
      const max = c.scrollWidth - c.clientWidth;
      const bar = c.nextElementSibling;
      bar.style.setProperty('--gc-thumb', `${(vis * 100).toFixed(2)}%`);
      bar.style.setProperty('--gc-pos', max > 0 ? `${((c.scrollLeft / max) * (1 / vis - 1) * 100).toFixed(2)}%` : '0');
    });
  }

  key(e) {
    if (this.overlay()) return; // a theme dialog (search) is open over the menu: it owns Esc and Tab
    if (e.key === 'Escape') {
      if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
      // focus goes back to the toggle only if it was inside the menu (a hover-opened panel leaves focus where it was)
      else if (this.active) this.close(this.active.contains(document.activeElement));
      return;
    }
    if (e.key !== 'Tab' || !this.hasAttribute('data-drawer-open')) return;
    this.backward = e.shiftKey;
    const scope = this.scope();
    if (!scope) return; // a screen hidden in the editor
    const f = this.focusables(scope);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (!scope.contains(document.activeElement)) (e.preventDefault(), first.focus());
    else if (e.shiftKey && document.activeElement === first) (e.preventDefault(), last.focus());
    else if (!e.shiftKey && document.activeElement === last) (e.preventDefault(), first.focus());
  }

  scope() {
    return this.view === 'home' ? this.querySelector('.gc-nav__home') : this.querySelector(`#gc-nav-${this.view}`);
  }

  focusables(scope) {
    return [...scope.querySelectorAll('a[href], button:not([disabled])')].filter((n) => n.getClientRects().length);
  }

  // Safari tabs between buttons only by default, so focus can step past the trap's last link: pull it back in
  guard(e) {
    if (!this.hasAttribute('data-drawer-open') || this.overlay()) return;
    const scope = this.scope();
    if (!scope || scope.contains(e.target)) return;
    const f = this.focusables(scope);
    (this.backward ? f[f.length - 1] : f[0])?.focus({ preventScroll: true });
  }

  // theme editor: selecting a block opens the screen that holds it and keeps it open while editing
  select(e) {
    if (!this.contains(e.target)) return;
    const panel = e.target.closest('.gc-nav__panel');
    this.pinned = true;
    this.hydrate(this);
    if (DESKTOP.matches) panel && this.open(panel.closest('.gc-nav__item'));
    else
      this.css.then(() => {
        this.openDrawer();
        this.show(panel ? panel.dataset.gcView : 'home', true);
      });
  }
}

if (!customElements.get('gc-nav')) customElements.define('gc-nav', GcNav);
